import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/Contas_CriaContaInput.ts';
import '../contracts/Contas_AtualizaContaInput.ts';
import '../contracts/Contas_AtualizaFormasPagamentoInput.ts';
import '../contracts/Contas_AtualizaUsuariosInput.ts';
import '../contracts/Contas_DeletaContaInput.ts';
import '../contracts/Contas_ValidateSetupInput.ts';
import '../contracts/Contas_AccountSummaryInput.ts';

import '../contracts/Contas_AccountSummaryResponse';
import '../contracts/Contas_AtualizaContaResponse';
import '../contracts/Contas_AtualizaFormasPagamentoResponse';
import '../contracts/Contas_AtualizaUsuariosResponse';
import '../contracts/Contas_BuscaContaResponse';
import '../contracts/Contas_CriaContaResponse';
import '../contracts/Contas_DashboardStatsResponse';
import '../contracts/Contas_DashboardStatsInput';
import '../contracts/Contas_DeletaContaResponse';
import '../contracts/Contas_ListaContasResponse';
import '../contracts/Contas_TransactionHistoryResponse';
import '../contracts/Contas_TransactionHistoryInput';
import '../contracts/Contas_ValidateSetupResponse';
import type { Contas_AtualizaContaInput } from '../contracts/Contas_AtualizaContaInput';
import type { Contas_AtualizaFormasPagamentoInput } from '../contracts/Contas_AtualizaFormasPagamentoInput';
import type { Contas_AtualizaUsuariosInput } from '../contracts/Contas_AtualizaUsuariosInput';
import type { Contas_CriaContaInput } from '../contracts/Contas_CriaContaInput';
export function useContas() {
  const isLoading = ref(false);
  const error = ref(null);

  const fetchContas = async () => {
    log('useContas', 'fetchContas', 'Fetching contas list');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get('/index.php/contas/contas/lista_contas', { responseContract: 'Contas_ListaContasResponse' });
      log('useContas', 'fetchContas', 'Response', response);

      const isSuccess = response.success;
      const data = response.dados || [];

      if (isSuccess) {
        return data;
      } else {
        throw new Error(response.error || 'Erro ao buscar contas');
      }
    } catch (err) {
      log('useContas', 'fetchContas', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchContaById = async (contaId: number) => {
    log('useContas', 'fetchContaById', 'Fetching conta', contaId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/contas/contas/busca_conta/${contaId}`, { responseContract: 'Contas_BuscaContaResponse' });
      log('useContas', 'fetchContaById', 'Response', response);

      const isSuccess = response.success;
      const data = response.dados || {};

      if (isSuccess) {
        return data;
      } else {
        throw new Error(response.error || 'Erro ao buscar conta');
      }
    } catch (err) {
      log('useContas', 'fetchContaById', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const createConta = async (contaData: Contas_CriaContaInput) => {
    log('useContas', 'createConta', 'Creating conta', contaData);
    isLoading.value = true;
    error.value = null;

    try {
      validate('Contas_CriaContaInput', contaData);
      const response = await httpService.post('/index.php/contas/contas/cria_conta', contaData, { responseContract: 'Contas_CriaContaResponse' });
      log('useContas', 'createConta', 'Response', response);

      const isSuccess = response.success;

      if (isSuccess) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao criar conta');
      }
    } catch (err) {
      log('useContas', 'createConta', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const updateConta = async (contaId: any, contaData: Contas_AtualizaContaInput) => {
    log('useContas', 'updateConta', 'Updating conta', contaId, contaData);
    isLoading.value = true;
    error.value = null;

    try {
      validate('Contas_AtualizaContaInput', contaData);
      const response = await httpService.post(`/index.php/contas/contas/atualiza_conta/${contaId}`, contaData, { responseContract: 'Contas_AtualizaContaResponse' });
      log('useContas', 'updateConta', 'Response', response);

      const isSuccess = response.success;

      if (isSuccess) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao atualizar conta');
      }
    } catch (err) {
      log('useContas', 'updateConta', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const deleteConta = async (contaId: number) => {
    log('useContas', 'deleteConta', 'Deleting conta', contaId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(`/index.php/contas/contas/deleta_conta/${contaId}`, null, { inputContract: 'Contas_DeletaContaInput', responseContract: 'Contas_DeletaContaResponse' });
      log('useContas', 'deleteConta', 'Response', response);

      const isSuccess = response.success;

      if (isSuccess) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao deletar conta');
      }
    } catch (err) {
      log('useContas', 'deleteConta', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const updateFormasPagamento = async (contaId: any, formasIds: any) => {
    log('useContas', 'updateFormasPagamento', 'Updating payment methods', contaId, formasIds);
    isLoading.value = true;
    error.value = null;

    try {
      const payload: Contas_AtualizaFormasPagamentoInput = { formas_pagamento_ids: formasIds };
      validate('Contas_AtualizaFormasPagamentoInput', payload);
      const response = await httpService.post(`/index.php/contas/contas/atualiza_formas_pagamento/${contaId}`, payload, { responseContract: 'Contas_AtualizaFormasPagamentoResponse' });
      log('useContas', 'updateFormasPagamento', 'Response', response);

      const isSuccess = response.success;

      if (isSuccess) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao atualizar formas de pagamento');
      }
    } catch (err) {
      log('useContas', 'updateFormasPagamento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const updateUsuarios = async (contaId: any, usuariosData: any) => {
    log('useContas', 'updateUsuarios', 'Updating users', contaId, usuariosData);
    isLoading.value = true;
    error.value = null;

    try {
      const payload: Contas_AtualizaUsuariosInput = { usuarios_data: usuariosData };
      validate('Contas_AtualizaUsuariosInput', payload);
      const response = await httpService.post(`/index.php/contas/contas/atualiza_usuarios/${contaId}`, payload, { responseContract: 'Contas_AtualizaUsuariosResponse' });
      log('useContas', 'updateUsuarios', 'Response', response);

      const isSuccess = response.success;

      if (isSuccess) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao atualizar usuários');
      }
    } catch (err) {
      log('useContas', 'updateUsuarios', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchDashboardStats = async () => {
    log('useContas', 'fetchDashboardStats', 'Fetching dashboard stats');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post('/index.php/contas/contas/dashboard_stats', null, { responseContract: 'Contas_DashboardStatsResponse', inputContract: 'Contas_DashboardStatsInput' });
      log('useContas', 'fetchDashboardStats', 'Response', response);

      const isSuccess = response.success;
      const data = response.dados || {};

      if (isSuccess) {
        return data;
      } else {
        throw new Error(response.error || 'Erro ao buscar estatísticas');
      }
    } catch (err) {
      log('useContas', 'fetchDashboardStats', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchAccountSummary = async (contaId: number) => {
    log('useContas', 'fetchAccountSummary', 'Fetching account summary', contaId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(`/index.php/contas/contas/account_summary/${contaId}`, null, { inputContract: 'Contas_AccountSummaryInput', responseContract: 'Contas_AccountSummaryResponse' });
      log('useContas', 'fetchAccountSummary', 'Response', response);

      const isSuccess = response.success;
      const data = response.dados || {};

      if (isSuccess) {
        return data;
      } else {
        throw new Error(response.error || 'Erro ao buscar resumo da conta');
      }
    } catch (err) {
      log('useContas', 'fetchAccountSummary', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchTransactionHistory = async (contaId: number) => {
    log('useContas', 'fetchTransactionHistory', 'Fetching transaction history', contaId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(`/index.php/contas/contas/transaction_history/${contaId}`, null, { responseContract: 'Contas_TransactionHistoryResponse', inputContract: 'Contas_TransactionHistoryInput' });
      log('useContas', 'fetchTransactionHistory', 'Response', response);

      const isSuccess = response.success;
      const data = response.dados || [];

      if (isSuccess) {
        return data;
      } else {
        throw new Error(response.error || 'Erro ao buscar histórico de transações');
      }
    } catch (err) {
      log('useContas', 'fetchTransactionHistory', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const validateSetup = async (contaId: number) => {
    log('useContas', 'validateSetup', 'Validating setup', contaId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(`/index.php/contas/contas/validate_setup/${contaId}`, null, { inputContract: 'Contas_ValidateSetupInput', responseContract: 'Contas_ValidateSetupResponse' });
      log('useContas', 'validateSetup', 'Response', response);

      const isSuccess = response.success;
      const data = response.dados || {};

      if (isSuccess) {
        return data;
      } else {
        throw new Error(response.error || 'Erro ao validar configuração');
      }
    } catch (err) {
      log('useContas', 'validateSetup', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Helper functions
  const getAccountTypeIcon = (tipo: any) => {
    const icons: any = {
      online: 'pi-globe',
      banco: 'pi-building',
      pos: 'pi-credit-card',
      dinheiro: 'pi-dollar',
      permuta: 'pi-refresh'
    };
    return icons[tipo] || 'pi-question-circle';
  };

  const getAccountTypeName = (tipo: any) => {
    const names: any = {
      online: 'Online',
      banco: 'Banco',
      pos: 'POS',
      dinheiro: 'Dinheiro',
      permuta: 'Permuta'
    };
    return names[tipo] || tipo;
  };

  const getAccountStatusBadge = (account: any) => {
    if (!account) return { type: 'secondary', text: 'Desconhecido', icon: 'pi-question-circle' };

    const validation = account.validation || {};

    // Inactive
    if (!validation.is_active) {
      return {
        type: 'danger',
        text: 'Inativa',
        icon: 'pi-times-circle',
        tooltip: 'Conta inativa'
      };
    }

    // Fully configured
    if (validation.is_complete) {
      return {
        type: 'success',
        text: 'Completa',
        icon: 'pi-check-circle',
        tooltip: 'Conta totalmente configurada'
      };
    }

    // Partially configured
    const issues = validation.issues || [];
    let text = 'Incompleta';
    let tooltip = 'Conta precisa de atenção: ';
    const tooltipParts = [];

    if (issues.includes('missing_payment_methods')) {
      tooltipParts.push('faltam formas de pagamento');
    }
    if (issues.includes('missing_users')) {
      tooltipParts.push('nenhum usuário atribuído');
    }

    tooltip += tooltipParts.join(', ');

    return {
      type: 'warning',
      text,
      icon: 'pi-exclamation-triangle',
      tooltip
    };
  };

  return {
    isLoading,
    error,
    // CRUD
    fetchContas,
    fetchContaById,
    createConta,
    updateConta,
    deleteConta,
    // Associations
    updateFormasPagamento,
    updateUsuarios,
    // Analytics
    fetchDashboardStats,
    fetchAccountSummary,
    fetchTransactionHistory,
    validateSetup,
    // Helpers
    getAccountTypeIcon,
    getAccountTypeName,
    getAccountStatusBadge
  };
}
